import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { dismissUploadPanel } from "../media-picker/model";
import { UploadBatchSummary, UploadingFile } from "../../uploading-files.model";

const AUTO_DISMISS_DELAY_MS = 1500;

export function useUploadPanelLifecycle(
  summary: UploadBatchSummary,
  uploads: UploadingFile[]
) {
  const dispatch = useDispatch();
  const toastedFailuresRef = useRef<string>();

  const { stage, failedCount, total } = summary;
  // Keyed on which files failed, not on the batch, so a retry that fails again
  // is still announced.
  const failureKey = uploads
    .filter((upload) => upload.phase === "failed")
    .map((upload) => upload.id)
    .join(",");

  useEffect(() => {
    if (stage !== "done" || failedCount > 0 || total === 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      dispatch(dismissUploadPanel() as never);
    }, AUTO_DISMISS_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [dispatch, stage, failedCount, total]);

  useEffect(() => {
    if (stage !== "done" || failedCount === 0) {
      return;
    }

    if (toastedFailuresRef.current === failureKey) {
      return;
    }

    toastedFailuresRef.current = failureKey;
    toast.error(
      failedCount === 1
        ? "1 file failed to upload"
        : `${failedCount} files failed to upload`
    );
  }, [stage, failedCount, failureKey]);
}
